import * as geojson from 'geojson';
import * as I from 'immutable';

import {fetchTileData} from 'app/components/AnalyzePolygonPopup/utils';
import {ApiFeatureData} from 'app/modules/Remote/Feature';
import {HydratedFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {
  MAX_DIRECTIONAL_TILE_COUNT_MASK,
  RasterCalculator,
  getTemplateUrl,
} from 'app/stores/RasterCalculationStore';
import * as featureUtils from 'app/utils/featureUtils';

export type LegendValueCoverage = Record<string, number>;

interface GetLegendValueCoverageForPolygonParams {
  cursor: string | null | undefined;
  featureCollection: HydratedFeatureCollection | null | undefined;
  featureData: I.ImmutableListOf<ApiFeatureData>;
  firebaseToken: string | null;
  legendSamplingLayerKey: string;
  polygon: geojson.Polygon | geojson.MultiPolygon | null;
  featureDatumOverride?: I.ImmutableOf<ApiFeatureData>;
  signal?: AbortSignal;
}

export async function getLegendValueCoverageForPolygon({
  cursor,
  featureCollection,
  featureData,
  firebaseToken,
  legendSamplingLayerKey,
  polygon,
  featureDatumOverride,
  signal,
}: GetLegendValueCoverageForPolygonParams): Promise<LegendValueCoverage | null> {
  if (!cursor || !polygon) {
    return null;
  }

  const featureDatum =
    featureDatumOverride ??
    (featureCollection
      ? featureUtils.getFeatureDatumFromLayerKey(
          featureData,
          legendSamplingLayerKey,
          cursor,
          featureCollection
        )
      : undefined);

  if (!featureDatum) {
    return null;
  }

  const legendTileData = fetchTileData(legendSamplingLayerKey, polygon, featureData, {
    maxDirectionalTileCount: MAX_DIRECTIONAL_TILE_COUNT_MASK,
  });
  if (legendTileData.status !== 'some') {
    return null;
  }

  const templateUrl = getTemplateUrl(featureDatum, legendSamplingLayerKey, firebaseToken);
  if (!templateUrl) {
    return null;
  }

  const calculator = await RasterCalculator.fromTiles(
    legendTileData.value,
    templateUrl,
    [0, 255],
    firebaseToken,
    undefined,
    signal
  );

  if (signal?.aborted) {
    return null;
  }

  const {weightedPixelsWithValueByValue} = calculator.calculate(polygon);
  const hasVisibleValues = Object.keys(weightedPixelsWithValueByValue).length > 0;

  return hasVisibleValues ? weightedPixelsWithValueByValue : null;
}
